import { useState, useEffect, useMemo, useCallback } from 'react';
import { Modal, Stack, Button, Group, Select, NumberInput, Text, Paper, TextInput, Alert } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { env } from '../../utils/env';
import { GroupBalance, GroupMember } from '../../types/group';
import { useSettlements, Settlement } from '../../hooks/useSettlements';

interface SettleUpModalProps {
  opened: boolean;
  onClose: () => void;
  onSuccess: () => void;
  groupId: number;
  balances: GroupBalance[];
  accessToken: string;
  existingSettlement?: {
    expense_id?: number;
    settlement_id?: number;
    from_member_id: number;
    to_member_id: number;
    amount: number;
    currency_code: string;
    notes?: string;
    payment_method?: string;
    payment_reference?: string;
  };
  groupMembers?: GroupMember[];
}

interface SettlementResponse {
  id: number;
  group_id: number;
  from_user_id: string;
  from_user_name: string;
  to_user_id: string;
  to_user_name: string;
  amount: string;
  currency_code: string;
  note?: string;
  payment_method?: string;
  payment_reference?: string;
  status: string;
  created_at: string;
}

interface FormValues {
  from_member_id: string;
  to_member_id: string;
  amount: number;
  payment_method: string;
  payment_reference: string;
  notes?: string;
}

interface MemberOption {
  id?: number;
  name?: string;
  value: string;
  label: string;
  member_id: number;
  user_id: string | null;
  email: string;
  display_name?: string;
  role?: string;
  status?: string;
}

// Define payment methods
const PAYMENT_METHODS = [
  { value: 'manual', label: 'Manual Payment' },
  { value: 'venmo', label: 'Venmo' },
  { value: 'zelle', label: 'Zelle' },
  { value: 'cashapp', label: 'Cash App' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'other', label: 'Other' },
];

// Debug utility
const DEBUG = env.DEBUG_MODE;
const debugLog = (...args: any[]) => {
  if (DEBUG) {
    console.log(...args);
  }
};

export const SettleUpModal = ({
  opened,
  onClose,
  onSuccess,
  groupId,
  balances,
  accessToken,
  existingSettlement,
  groupMembers = []
}: SettleUpModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isEditMode = !!existingSettlement;
  const [showGhostWarning, setShowGhostWarning] = useState(false);
  const [ghostMemberName, setGhostMemberName] = useState('');
  const [showMissingUserIdWarning, setShowMissingUserIdWarning] = useState(false);
  const [missingUserIdMemberName, setMissingUserIdMemberName] = useState('');
  const [members, setMembers] = useState<MemberOption[]>([]);

  // Use the settlements hook
  const {
    createSettlement,
    updateSettlement,
    loading: settlementLoading
  } = useSettlements({ groupId, accessToken });

  // Helper to suggest an amount based on balances
  const getSuggestedAmount = useCallback((): number => {
    if (!balances || balances.length === 0) return 0;

    // If existing settlement, use that amount
    if (existingSettlement) return existingSettlement.amount;

    // Otherwise use the first balance amount as a suggestion
    const balance = balances[0];
    return typeof balance.amount === 'string' ? parseFloat(balance.amount) : balance.amount;
  }, [balances, existingSettlement]);

  // Initialize form with validation
  const form = useForm<FormValues>({
    initialValues: {
      from_member_id: existingSettlement ? existingSettlement.from_member_id.toString() : '',
      to_member_id: existingSettlement ? existingSettlement.to_member_id.toString() : '',
      amount: existingSettlement ? existingSettlement.amount : 0,
      payment_method: existingSettlement?.payment_method || 'manual',
      payment_reference: existingSettlement?.payment_reference || '',
      notes: existingSettlement?.notes || '',
    },
    validate: {
      from_member_id: (value) => {
        if (!value) return 'Payer is required';

        // Validate based only on status, not user_id
        const member = members.find(m => m.id?.toString() === value || m.member_id?.toString() === value);
        if (!member) return 'Invalid payer selected';

        // Only check status
        if (member.status === 'ghost') {
          // Add logging for ghost user rejection
          console.warn('[SettleUpModal] Form validation rejected ghost user for from_member_id:',
            { memberId: value, status: member.status, name: member.name });
          return `${member.name || 'This member'} is a ghost user and cannot be included in settlements. They need to join with a real account first.`;
        }

        return null;
      },
      to_member_id: (value, values) => {
        if (!value) return 'Recipient is required';
        if (value === values.from_member_id) return 'Payer and recipient must be different';

        // Validate based only on status, not user_id
        const member = members.find(m => m.id?.toString() === value || m.member_id?.toString() === value);
        if (!member) return 'Invalid recipient selected';

        // Only check status
        if (member.status === 'ghost') {
          // Add logging for ghost user rejection
          console.warn('[SettleUpModal] Form validation rejected ghost user for to_member_id:',
            { memberId: value, status: member.status, name: member.name });
          return `${member.name || 'This member'} is a ghost user and cannot be included in settlements. They need to join with a real account first.`;
        }

        return null;
      },
      amount: (value) => (value <= 0 ? 'Amount must be greater than 0' : null),
    },
  });

  // Auto-suggest amount and check warnings when users change
  const handleUserChange = useCallback(() => {
    // Only auto-adjust the amount if we're not in edit mode
    if (!isEditMode) {
      const suggestedAmount = getSuggestedAmount();
      if (suggestedAmount > 0) {
        form.setFieldValue('amount', suggestedAmount);
      }
    }

    // Check if selected members have user_ids or are ghosts
    const { from_member_id, to_member_id } = form.values;
    const fromMember = members.find(m => m.id?.toString() === from_member_id);
    const toMember = members.find(m => m.id?.toString() === to_member_id);

    // Clear the warnings first
    setShowGhostWarning(false);
    setShowMissingUserIdWarning(false);

    // Check only for ghost status
    if (fromMember && fromMember.status === 'ghost') {
      setGhostMemberName(fromMember.name || 'Unknown member');
      setShowGhostWarning(true);
    } else if (toMember && toMember.status === 'ghost') {
      setGhostMemberName(toMember.name || 'Unknown member');
      setShowGhostWarning(true);
    }

    // Check for missing user_id (only if not a ghost)
    if (fromMember && !fromMember.user_id && fromMember.status !== 'ghost') {
      setMissingUserIdMemberName(fromMember.name || fromMember.email || 'Unknown member');
      setShowMissingUserIdWarning(true);
    } else if (toMember && !toMember.user_id && toMember.status !== 'ghost') {
      setMissingUserIdMemberName(toMember.name || toMember.email || 'Unknown member');
      setShowMissingUserIdWarning(true);
    }
  }, [isEditMode, getSuggestedAmount, form, members, setGhostMemberName, setShowGhostWarning, setMissingUserIdMemberName, setShowMissingUserIdWarning]);

  const handleClose = useCallback(() => {
    form.reset();
    onClose();
  }, [form, onClose]);

  const handleSubmit = async (values: FormValues) => {
    setIsSubmitting(true);
    let settlementSuccess = false; // Flag to track success

    try {
      // Get the selected members
      const fromMember = members.find(m => m.id?.toString() === values.from_member_id);
      const toMember = members.find(m => m.id?.toString() === values.to_member_id);

      if (!fromMember || !toMember) {
        console.error('[SettleUpModal] Could not find selected members:', {
          values,
          selectedFromId: values.from_member_id,
          selectedToId: values.to_member_id,
          availableMembers: members.map(m => ({ id: m.id, name: m.name }))
        });
        throw new Error('Selected members not found');
      }

      // Log information about selected members
      console.log('[SettleUpModal] Selected members for settlement:', {
        from: {
          id: fromMember.id,
          name: fromMember.name,
          user_id: fromMember.user_id,
          status: fromMember.status
        },
        to: {
          id: toMember.id,
          name: toMember.name,
          user_id: toMember.user_id,
          status: toMember.status
        }
      });

      // Form validation should already prevent these cases, but add extra validation here as a safety measure
      if (fromMember.status === 'ghost' || toMember.status === 'ghost') {
        const ghostMember = fromMember.status === 'ghost' ? fromMember : toMember;
        console.warn('[SettleUpModal] Ghost user validation check failed:', ghostMember);
        throw new Error(`Cannot create settlement with ghost user: ${ghostMember.name || ghostMember.email}`);
      }

      // If we're editing, use the update endpoint
      if (isEditMode) {
        const settlementId = existingSettlement?.settlement_id || existingSettlement?.expense_id;
        if (!settlementId) {
          throw new Error('No settlement ID provided for edit');
        }

        console.log(`[SettleUpModal] Editing settlement with ID: ${settlementId}`);

        const updateData = {
          note: values.notes,
          payment_method: values.payment_method,
          payment_reference: values.payment_reference || values.payment_method,
        };

        console.log('[SettleUpModal] Sending update with data:', updateData);

        const updatedSettlement = await updateSettlement(settlementId, updateData);
        if (!updatedSettlement) {
          throw new Error('Failed to update settlement');
        }

        notifications.show({
          title: 'Success',
          message: 'Settlement updated successfully',
          color: 'green',
        });

        settlementSuccess = true;
      } else {
        // Creating a new settlement
        // Ensure user_id fields exist and are not null before creating settlement
        if (!fromMember.user_id || !toMember.user_id) {
          const missingMember = !fromMember.user_id ? fromMember : toMember;
          console.error('[SettleUpModal] Cannot create settlement: Member missing user_id:', missingMember);
          throw new Error(`Cannot create settlement for ${missingMember.name || missingMember.email}. Missing user ID.`);
        }

        // Create properly typed data object using non-null assertions now that we've checked
        const createData = {
          from_user_id: fromMember.user_id!, // Non-null assertion is safe due to check above
          to_user_id: toMember.user_id!, // Non-null assertion is safe due to check above
          amount: values.amount,
          currency_code: balances[0]?.currency_code || 'USD',
          note: values.notes,
          payment_method: values.payment_method,
          payment_reference: values.payment_reference || values.payment_method,
        };

        console.log('[SettleUpModal] Sending create with data:', createData);

        const newSettlement = await createSettlement(createData);
        if (!newSettlement) {
          throw new Error('Failed to create settlement');
        }

        notifications.show({
          title: 'Success',
          message: 'Settlement created successfully',
          color: 'green',
        });

        settlementSuccess = true;
      }

      if (settlementSuccess) {
        handleClose();
        if (onSuccess) {
          onSuccess();
        }
      }
    } catch (error) {
      console.error('[SettleUpModal] Error handling settlement:', error);
      settlementSuccess = false; // Ensure flag is false on error
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'An unexpected error occurred',
        color: 'red',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  // Simplified useEffect to derive members primarily from groupMembers if available,
  // or fallback to balances. Avoids calling setMembers multiple times per run.
  useEffect(() => {
    debugLog('[SettleUpModal] useEffect triggered: Deriving members list.');
    let memberOptions: MemberOption[] = [];

    if (groupMembers && groupMembers.length > 0) {
      debugLog('[SettleUpModal] Using groupMembers from props:', groupMembers);
      memberOptions = groupMembers.map(member => {
        // Attempt to find matching user_id from balances if not already present on GroupMember
        let userId = member.user_id || null;
        if (!userId && balances && balances.length > 0) {
          const matchingBalance = balances.find(b => b.from_member_id === member.id || b.to_member_id === member.id);
          if (matchingBalance) {
            userId = matchingBalance.from_member_id === member.id
              ? matchingBalance.from_user_id
              : matchingBalance.to_user_id;
          }
        }
        return {
          value: member.id.toString(),
          label: member.display_name || member.email || `Member ${member.id}`,
          member_id: member.id,
          user_id: userId,
          email: member.email || '',
          display_name: member.display_name,
          status: member.status || 'unknown',
          id: member.id, // Keep for compatibility if needed elsewhere
          name: member.display_name || member.email || `Member ${member.id}` // Keep for compatibility
        };
      });
    } else if (balances && balances.length > 0) {
      debugLog('[SettleUpModal] Fallback: Deriving members from balances.');
      const uniqueMembers = new Map<number, MemberOption>();
      balances.forEach(balance => {
        if (balance.from_member_id && !uniqueMembers.has(balance.from_member_id)) {
          uniqueMembers.set(balance.from_member_id, {
            value: balance.from_member_id.toString(),
            label: balance.from_member_name || `Member ${balance.from_member_id}`,
            member_id: balance.from_member_id,
            id: balance.from_member_id,
            name: balance.from_member_name || `Member ${balance.from_member_id}`,
            user_id: balance.from_user_id || null,
            email: '',
            display_name: balance.from_member_name,
            status: balance.from_member_status || 'unknown'
          });
        }
        if (balance.to_member_id && !uniqueMembers.has(balance.to_member_id)) {
          uniqueMembers.set(balance.to_member_id, {
            value: balance.to_member_id.toString(),
            label: balance.to_member_name || `Member ${balance.to_member_id}`,
            member_id: balance.to_member_id,
            id: balance.to_member_id,
            name: balance.to_member_name || `Member ${balance.to_member_id}`,
            user_id: balance.to_user_id || null,
            email: '',
            display_name: balance.to_member_name,
            status: balance.to_member_status || 'unknown'
          });
        }
      });
      memberOptions = Array.from(uniqueMembers.values());
    }

    debugLog('[SettleUpModal] Setting members state with derived options:', memberOptions);

    // Avoid unnecessary state updates that can lead to re-render loops
    setMembers((prev) => {
      // Quick length check first
      if (prev.length === memberOptions.length) {
        // Shallow compare each option; if all keys match, skip state update
        const isSame = prev.every((p, idx) => {
          const n = memberOptions[idx];
          return (
            p.value === n.value &&
            p.label === n.label &&
            p.user_id === n.user_id &&
            p.status === n.status
          );
        });
        if (isSame) {
          debugLog('[SettleUpModal] Derived members identical to current state – skipping setMembers');
          return prev; // Return previous state to avoid update
        }
      }

      debugLog('[SettleUpModal] Members changed – updating state');
      return memberOptions;
    });
  }, [balances, groupMembers]); // Dependency array only includes props affecting member derivation

  // useEffect for setting initial form values based on the first balance
  // Runs only when conditions are met and form isn't already populated or in edit mode
  useEffect(() => {
    // Only run if NOT editing, members are derived, balances exist, and form fields are empty
    if (!isEditMode && members.length > 0 && balances && balances.length > 0 && !form.values.from_member_id && !form.values.to_member_id) {
      const firstBalance = balances[0];
      const fromOption = members.find(m => m.member_id === firstBalance.from_member_id);
      const toOption = members.find(m => m.member_id === firstBalance.to_member_id);

      // Ensure both members are valid (exist and are not ghosts) before setting defaults
      if (fromOption && fromOption.status !== 'ghost' && fromOption.id &&
        toOption && toOption.status !== 'ghost' && toOption.id) {
        const initialAmount = getSuggestedAmount(); // Use memoized helper
        form.setValues(prev => ({
          ...prev,
          from_member_id: fromOption.id!.toString(),
          to_member_id: toOption.id!.toString(),
          amount: initialAmount > 0 ? initialAmount : 0, // Ensure amount is not negative
        }));
        debugLog('[SettleUpModal] Set initial form values based on first balance.');
      } else {
        debugLog('[SettleUpModal] Could not set initial form values - first balance members not valid/found.');
      }
    }
  }, [isEditMode, members, balances, getSuggestedAmount, form.setValues, form.values.from_member_id, form.values.to_member_id]); // Dependencies relevant to initial value setting

  // Get active members only (for selection purposes) - Filter based on status
  const activeMembers = useMemo(() => {
    const filtered = members.filter(m => m.status !== 'ghost');
    // Add logging inside useMemo to see when it recalculates and the result
    console.log(`[SettleUpModal] useMemo (activeMembers): Recalculating based on members state. Input members: ${members.length}, Filtered active: ${filtered.length}`);
    console.log('[SettleUpModal] Final activeMembers for dropdowns:', filtered.map(m => ({ id: m.id, name: m.name, status: m.status })));
    return filtered;
  }, [members]); // Depends only on members state

  // Debug balances prop changes
  useEffect(() => {
    console.log('[SettleUpModal] Balance details prop updated:');
    balances.forEach((balance, index) => {
      console.log(`Balance ${index}:`, {
        from: {
          id: balance.from_member_id,
          name: balance.from_member_name,
          user_id: balance.from_user_id || 'null',
          status: balance.from_member_status || 'unknown'
        },
        to: {
          id: balance.to_member_id,
          name: balance.to_member_name,
          user_id: balance.to_user_id || 'null',
          status: balance.to_member_status || 'unknown'
        },
        amount: balance.amount,
        currency: balance.currency_code
      });
    });
  }, [balances]);

  const hasEnoughValidMembers = activeMembers.length >= 2;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={isEditMode ? "Edit Settlement" : "Settle Up"}
      size="md"
    >
      <Stack>
        {members.some(m => m.status === 'ghost') && !showGhostWarning && (
          <Alert title="Some Members Cannot Participate in Settlements" color="blue">
            <Text size="sm">
              This group contains members without accounts (labeled as "ghost users").
              These members' balances will be displayed but they cannot participate in settlements
              until they register and join the group with an account.
            </Text>
          </Alert>
        )}

        {showGhostWarning && (
          <Alert title="Cannot Create Settlement with Selected Member" color="red" withCloseButton onClose={() => setShowGhostWarning(false)}>
            <Text size="sm">
              <strong>{ghostMemberName}</strong> is a member without an account and cannot participate in settlements.
              Please select a different member for this settlement.
            </Text>
          </Alert>
        )}

        {showMissingUserIdWarning && (
          <Alert title="Settlement May Fail" color="yellow" withCloseButton onClose={() => setShowMissingUserIdWarning(false)}>
            <Text size="sm">
              <strong>{missingUserIdMemberName}</strong> may not be fully registered in the system.
              The settlement could fail if this member hasn't completed their registration or accepted their invitation.
            </Text>
          </Alert>
        )}

        {!isEditMode && (
          <>
            <Text fw={500}>Current Balances</Text>
            <Stack>
              {balances.map((balance) => (
                <Paper key={`${balance.from_member_id}-${balance.to_member_id}`} p="xs" withBorder>
                  <Group justify="space-between">
                    <Text size="sm">
                      {balance.from_member_name} owes {balance.to_member_name}
                    </Text>
                    <Text fw={500}>
                      {formatCurrency(balance.amount, balance.currency_code)}
                    </Text>
                  </Group>
                  {(balance.from_member_status === 'ghost' || balance.to_member_status === 'ghost') && (
                    <Text size="xs" color="red" mt={5}>
                      {(balance.from_member_status === 'ghost' && balance.to_member_status === 'ghost') ?
                        "Both members are ghost users (cannot settle up)" :
                        `${balance.from_member_status === 'ghost' ? balance.from_member_name : balance.to_member_name} is a ghost user (cannot settle up)`}
                    </Text>
                  )}
                </Paper>
              ))}
            </Stack>
          </>
        )}

        {!hasEnoughValidMembers && !isEditMode && ( // Only show this warning when creating, not editing
          <Alert color="red" title="Missing Members">
            You need at least two active members to create a settlement.
            {activeMembers.length === 0
              ? " No active members found."
              : ` Only ${activeMembers.length} active member${activeMembers.length === 1 ? '' : 's'} found.`}
          </Alert>
        )}

        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack>
            <Select
              label="Payer"
              placeholder="Select who paid"
              withAsterisk
              // Use activeMembers which is derived via useMemo
              data={activeMembers.map(m => ({
                value: m.id?.toString() || '', // Ensure value is string and non-null
                label: m.label, // Use derived label
                disabled: m.status === 'ghost',
              }))}
              onChange={(value) => {
                form.setFieldValue('from_member_id', value || '');
                handleUserChange(); // Call memoized handler
              }}
              value={form.values.from_member_id}
              error={form.errors.from_member_id}
              disabled={isEditMode || !hasEnoughValidMembers} // Disable if editing or not enough members
              description={!isEditMode ? "Ghost users cannot participate in settlements" : ""}
            />

            <Select
              label="Recipient"
              placeholder="Select who received"
              withAsterisk
              // Use activeMembers which is derived via useMemo
              data={activeMembers.map(m => ({
                value: m.id?.toString() || '', // Ensure value is string and non-null
                label: m.label, // Use derived label
                disabled: m.status === 'ghost',
              }))}
              onChange={(value) => {
                form.setFieldValue('to_member_id', value || '');
                handleUserChange(); // Call memoized handler
              }}
              value={form.values.to_member_id}
              error={form.errors.to_member_id}
              disabled={isEditMode || !hasEnoughValidMembers} // Disable if editing or not enough members
              description={!isEditMode ? "Ghost users cannot participate in settlements" : ""}
            />

            <Group align="flex-end">
              <NumberInput
                label="Amount"
                placeholder="Enter amount"
                withAsterisk
                min={0}
                step={0.01}
                style={{ flex: 1 }}
                {...form.getInputProps('amount')}
                description={!isEditMode ? "You can enter any amount up to the full balance" : ""}
                disabled={!hasEnoughValidMembers && !isEditMode} // Disable if not enough members when creating
              />
              <Button
                variant="light"
                onClick={() => {
                  const fullAmount = getSuggestedAmount(); // Use memoized helper
                  if (fullAmount > 0) {
                    form.setFieldValue('amount', fullAmount);
                  }
                }}
                disabled={isEditMode || !hasEnoughValidMembers} // Disable if editing or not enough members
              >
                Full Amount
              </Button>
            </Group>

            <Select
              label="Payment Method"
              placeholder="How was this paid?"
              withAsterisk
              data={PAYMENT_METHODS}
              {...form.getInputProps('payment_method')}
              disabled={!hasEnoughValidMembers && !isEditMode} // Disable if not enough members when creating
            />

            <TextInput
              label="Payment Reference"
              placeholder="Reference number or description (e.g. 'Transaction #1234')"
              {...form.getInputProps('payment_reference')}
              disabled={!hasEnoughValidMembers && !isEditMode} // Disable if not enough members when creating
            />

            <TextInput
              label="Notes"
              placeholder="Add optional notes about this settlement"
              {...form.getInputProps('notes')}
              disabled={!hasEnoughValidMembers && !isEditMode} // Disable if not enough members when creating
            />

            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={isSubmitting || settlementLoading}
                color={isEditMode ? "blue" : "green"}
                // Disable submit if not enough valid members when creating
                disabled={(!hasEnoughValidMembers && !isEditMode) || isSubmitting || settlementLoading}
              >
                {isEditMode ? 'Update Settlement' : 'Record Settlement'}
              </Button>
            </Group>
          </Stack>
        </form>
      </Stack>
    </Modal>
  );
}; 